<template>
  <Modal classes="google-location" @close="close">
    <button class="google-location__close" type="button" @click="close">
      ×
    </button>

    <h3 class="google-location__title">
      Потърсете или кликнете върху картата за да изберете локацията на обекта
    </h3>
    <div class="google-location__search">
      <span class="google-location__search-icon sienit">N</span>
      <input
        ref="locationSearchInput"
        class="google-location__search-input"
        type="text"
        placeholder="Търсене в Google Maps"
        @keydown.enter.prevent
      />
    </div>
    <div ref="locationMap" class="google-location__map">
      <div v-if="isLoading" class="google-location__map-state">
        Зареждане на карта...
      </div>
      <div
        v-if="errorMessage"
        class="google-location__map-state google-location__map-state--error"
      >
        {{ errorMessage }}
      </div>
    </div>
    <p class="google-location__preview">
      {{
        pendingLocation.latitude && pendingLocation.longitude
          ? `${pendingLocation.latitude},${pendingLocation.longitude}`
          : "Локация не е избрана"
      }}
    </p>
    <div class="google-location__actions">
      <button class="btn btn-close" type="button" @click="close">Отказ</button>
      <button
        class="btn btn-new"
        type="button"
        :disabled="!pendingLocation.latitude || !pendingLocation.longitude"
        @click="confirmSelection"
      >
        Избери
      </button>
    </div>
  </Modal>
</template>

<script>
import Modal from '../components/Modal.vue';

const defaultCenter = {
  latitude: '42.149481547819434',
  longitude: '24.75097507259718'
};

export default {
  components: {
    Modal
  },
  props: {
    value: {
      type: Object,
      default: () => ({ latitude: '', longitude: '' })
    },
    zoom: {
      type: Number,
      default: 13
    },
  },
  data() {
    return {
      map: null,
      marker: null,
      autocomplete: null,
      pendingLocation: {
        latitude: '',
        longitude: ''
      },
      isLoading: false,
      errorMessage: ''
    };
  },
  computed: {
    apiKey() {
      return process.env.VUE_APP_GOOGLE_MAPS_API_KEY || '';
    },
    mapCenter() {
      return {
        latitude: this.value.latitude || defaultCenter.latitude,
        longitude: this.value.longitude || defaultCenter.longitude
      };
    }
  },
  mounted() {
    this.pendingLocation = {
      latitude: this.value.latitude || '',
      longitude: this.value.longitude || ''
    };
    this.initMap();
  },
  methods: {
    close() {
      this.$emit('close');
    },
    confirmSelection() {
      if (!this.pendingLocation.latitude || !this.pendingLocation.longitude) return;
      this.$emit('select', Object.assign({}, this.pendingLocation));
    },
    loadGoogleMaps() {
      if (window.google && window.google.maps && window.google.maps.places) {
        return Promise.resolve(window.google);
      }
      if (window.__sienitGoogleMapsPromise) {
        return window.__sienitGoogleMapsPromise;
      }
      if (!this.apiKey) {
        return Promise.reject(new Error('Missing Google Maps API key.'));
      }

      window.__sienitGoogleMapsPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.apiKey)}&libraries=places&language=bg`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.google);
        script.onerror = () => reject(new Error('Unable to load Google Maps.'));
        document.head.appendChild(script);
      });

      return window.__sienitGoogleMapsPromise;
    },
    initMap() {
      if (!this.$refs.locationMap) return;
      this.isLoading = true;
      this.errorMessage = '';

      this.loadGoogleMaps()
        .then((google) => {
          const center = {
            lat: parseFloat(this.pendingLocation.latitude || this.mapCenter.latitude),
            lng: parseFloat(this.pendingLocation.longitude || this.mapCenter.longitude)
          };
          this.map = new google.maps.Map(this.$refs.locationMap, {
            center,
            zoom: this.zoom,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true
          });
          this.marker = new google.maps.Marker({
            map: this.map,
            position: center,
            draggable: true,
            visible: !!(this.pendingLocation.latitude && this.pendingLocation.longitude)
          });
          this.map.addListener('click', (event) => {
            this.setPendingLocationFromLatLng(event.latLng);
          });
          this.marker.addListener('dragend', (event) => {
            this.setPendingLocationFromLatLng(event.latLng);
          });
          this.initAutocomplete(google);
        })
        .catch(() => {
          this.errorMessage = 'Google Maps не може да се зареди. Проверете GOOGLE_MAPS_API_KEY.';
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    initAutocomplete(google) {
      if (!this.$refs.locationSearchInput) return;
      this.autocomplete = new google.maps.places.Autocomplete(
        this.$refs.locationSearchInput,
        {
          fields: ['geometry', 'name', 'formatted_address'],
          types: ['geocode', 'establishment']
        }
      );
      this.autocomplete.bindTo('bounds', this.map);
      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) {
          this.errorMessage = 'Не е намерена локация за избраното място.';
          return;
        }
        this.errorMessage = '';
        this.setPendingLocationFromLatLng(place.geometry.location);
        if (place.geometry.viewport) {
          this.map.fitBounds(place.geometry.viewport);
        } else {
          this.map.setCenter(place.geometry.location);
          this.map.setZoom(this.zoom);
        }
      });
    },
    setPendingLocationFromLatLng(latLng) {
      const location = {
        latitude: latLng.lat().toFixed(7),
        longitude: latLng.lng().toFixed(7)
      };
      this.pendingLocation = location;
      if (this.marker) {
        this.marker.setPosition(latLng);
        this.marker.setVisible(true);
      }
      if (this.map) {
        this.map.panTo(latLng);
      }
    }
  }
};
</script>

<style lang="scss">
.miv-modal-wrapper .modal.google-location {
  background-color: #fff;
  border-radius: 14px;
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.2);
  max-width: 620px;
  padding: 16px 14px 14px;
  position: relative;
  width: 100%;
}

.google-location {
  &__title {
    color: #333;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.35;
    margin: 0 38px 14px 0;
  }

  &__search {
    align-items: center;
    border: 1px solid #d8d2bd;
    border-radius: 4px;
    display: flex;
    height: 42px;
    margin-bottom: 12px;
    padding: 0 12px;
  }

  &__search-icon {
    color: #8d3506;
    font-size: 18px;
  }

  &__search-input {
    border: 0;
    color: #222;
    flex: 1;
    font-size: 15px;
    height: 100%;
    outline: none;
  }

  &__close {
    background: transparent;
    border: 0;
    color: #666;
    cursor: pointer;
    font-size: 24px;
    height: 30px;
    line-height: 1;
    position: absolute;
    right: 10px;
    top: 14px;
    width: 30px;

    &:hover {
      color: #333;
    }
  }

  &__map {
    border: 1px solid #d8d2bd;
    height: 410px;
    overflow: hidden;
    position: relative;
  }

  &__map-state {
    align-items: center;
    background-color: rgba(255, 255, 255, 0.92);
    bottom: 0;
    color: #333;
    display: flex;
    font-weight: 700;
    justify-content: center;
    left: 0;
    padding: 18px;
    position: absolute;
    right: 0;
    text-align: center;
    top: 0;
    z-index: 1;

    &--error {
      color: #8d3506;
    }
  }

  &__preview {
    color: #555;
    font-size: 13px;
    margin: 10px 0 0;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 14px;

    .btn {
      margin-left: 10px;

      &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
      }
    }
  }
}

@media screen and (max-width: 1000px) {
  .miv-modal-wrapper .modal.google-location {
    border-radius: 12px;
    padding: 14px 12px 12px;
  }

  .google-location {
    &__title {
      font-size: 15px;
    }

    &__map {
      height: 320px;
    }
  }
}
</style>

<style>
.pac-container {
  z-index: 2000 !important;
}
</style>
